import { useEffect, useState } from "react"
import { useParams } from "react-router"
import { getServerConfig, updateServerConfig } from "../api/config.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { useToast } from "../components/ui/Toast.js"
import { Coachmark } from "../components/onboarding/Coachmark.js"
import { useFirstVisit } from "../components/onboarding/useFirstVisit.js"
import { getHint } from "../components/onboarding/hints.js"

type PageState = { status: "loading" } | { status: "ready" } | { status: "error"; message: string }

/** `/servers/[id]/config` (FR seccion 19 item 10): `server.properties` como texto plano, ver routes/config.ts. */
export function ServerConfigPage() {
  const { serverId } = useParams<{ serverId: string }>()
  const [state, setState] = useState<PageState>({ status: "loading" })
  const [content, setContent] = useState("")
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const [isFirstVisit, dismissFirstVisit] = useFirstVisit("server-config")

  useEffect(() => {
    if (!serverId) return
    setState({ status: "loading" })
    getServerConfig(serverId)
      .then(({ content }) => {
        setContent(content ?? "")
        setState({ status: "ready" })
      })
      .catch((err) => setState({ status: "error", message: friendlyError(err).message }))
  }, [serverId])

  async function handleSave() {
    if (!serverId) return
    setSaving(true)
    try {
      await updateServerConfig(serverId, content)
      toast.show("Configuración guardada. Puede requerir reiniciar el servidor.", "success")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setSaving(false)
    }
  }

  if (state.status === "loading") {
    return (
      <div style={{ display: "grid", placeItems: "center", padding: "var(--space-12) 0" }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (state.status === "error") {
    return (
      <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
        {state.message}
      </p>
    )
  }

  const editor = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        spellCheck={false}
        style={{
          fontFamily: "var(--font-mono, ui-monospace, monospace)",
          fontSize: "var(--text-sm)",
          lineHeight: 1.6,
          background: "var(--bg-primary)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-md)",
          padding: "var(--space-4)",
          minHeight: "50vh",
          color: "var(--text-primary)",
          resize: "vertical",
        }}
      />
      <Button loading={saving} onClick={handleSave} style={{ alignSelf: "flex-start" }}>
        Guardar
      </Button>
    </div>
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Configuración</h1>
        <p style={{ color: "var(--text-muted)" }}>server.properties del servidor.</p>
      </div>
      {isFirstVisit ? (
        <Coachmark title={getHint("server-config").title} description={getHint("server-config").description} onDismiss={dismissFirstVisit}>
          {editor}
        </Coachmark>
      ) : (
        editor
      )}
    </div>
  )
}
